import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Group, Input, Loader, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";

const normalizeSelectedIds = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const id = typeof item === "object" ? item?._id : item;
      return id ? String(id) : null;
    })
    .filter(Boolean);
};

let cachedCategories = null;
let categoriesRequest = null;

const loadProductCategories = async (api) => {
  if (cachedCategories) return cachedCategories;
  if (!categoriesRequest) {
    categoriesRequest = api
      .search({
        apiEntity: "productCategory",
        pageSize: 200,
        sort: { by: "name", order: "asc" },
      })
      .then((result) => {
        cachedCategories = result?.docs ?? [];
        return cachedCategories;
      })
      .catch((error) => {
        categoriesRequest = null;
        throw error;
      });
  }
  return categoriesRequest;
};

const ProductCategoryCheckboxGroup = ({
  form,
  name,
  label,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  formMode,
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [categories, setCategories] = useState(() => cachedCategories ?? []);
  const [loading, setLoading] = useState(() => !cachedCategories);
  const defaultedRef = useRef(false);
  const recordIdRef = useRef(form?.values?._id);

  useEffect(() => {
    if (recordIdRef.current === form?.values?._id) return;
    recordIdRef.current = form?.values?._id;
    defaultedRef.current = false;
  }, [form?.values?._id]);

  const selectedIds = useMemo(() => {
    const currentValue = value ?? form?.values?.[name];
    return normalizeSelectedIds(currentValue);
  }, [form?.values?.[name], name, value]);

  const setSelectedIds = (nextValue, { markEdited = true } = {}) => {
    if (markEdited) {
      form?.markUserEdited?.();
    }
    if (onChange) {
      onChange(nextValue);
      return;
    }
    form?.setFieldValue(name, nextValue);
  };

  useEffect(() => {
    let active = true;

    const fetchCategories = async () => {
      try {
        const docs = await loadProductCategories(api);
        if (!active) return;
        setCategories(docs);
      } catch (error) {
        console.error(error);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (cachedCategories) {
      setCategories(cachedCategories);
      setLoading(false);
      return undefined;
    }

    fetchCategories();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || !categories.length || readOnly || disabled || defaultedRef.current) {
      return;
    }

    if (selectedIds.length > 0) {
      defaultedRef.current = true;
      return;
    }

    const isAddMode = formMode === "add";
    const isEditWithLoadedRecord = formMode === "edit" && form?.values?._id;
    if (!isAddMode && !isEditWithLoadedRecord) return;

    defaultedRef.current = true;
    const defaultIds = categories.map((category) => String(category._id));
    form?.setFieldValue(name, defaultIds);
  }, [
    categories,
    disabled,
    form,
    form?.values?._id,
    formMode,
    loading,
    name,
    readOnly,
    selectedIds.length,
  ]);

  const handleChange = (nextValue) => {
    setSelectedIds(nextValue);
  };

  return (
    <Input.Wrapper label={label ? t(label) : undefined}>
      {loading ? (
        <Loader size="sm" />
      ) : readOnly || disabled ? (
        <Text size="sm">
          {categories
            .filter((category) => selectedIds.includes(String(category._id)))
            .map((category) =>
              category.cname ? `${category.name} ${category.cname}` : category.name
            )
            .join(", ") || "-"}
        </Text>
      ) : (
        <>
          <Checkbox.Group value={selectedIds} onChange={handleChange}>
            <Group spacing="lg">
              {categories.map((category) => (
                <Checkbox
                  key={category._id}
                  value={String(category._id)}
                  label={
                    category.cname ? `${category.name} ${category.cname}` : category.name
                  }
                />
              ))}
            </Group>
          </Checkbox.Group>
          {!categories.length && (
            <Stack spacing={4} mt="xs">
              <Text size="xs" color="dimmed">
                {t("No product categories found")}
              </Text>
            </Stack>
          )}
        </>
      )}
    </Input.Wrapper>
  );
};

export default ProductCategoryCheckboxGroup;
